import type { MediaFormat } from '@prisma/client'

import { cn } from '~/utils/classnames'

const ProductPageLayoutRoot = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      className={cn(
        'bg-background flex min-h-full flex-col pt-0! @lg:grid @lg:grid-cols-[auto_minmax(40vw,1fr)] @lg:gap-6 @lg:p-6'
      )}
      {...props}
    >
      {children}
    </div>
  )
}

interface ProductPageLayoutMediaProps extends React.ComponentPropsWithoutRef<'div'> {
  format: MediaFormat
}

const ProductPageLayoutMedia = ({
  format = 'square',
  className,
  children,
  ...props
}: ProductPageLayoutMediaProps) => {
  return (
    <div
      className={cn(
        'relative h-full w-full overflow-hidden',
        format === 'landscape' && 'aspect-[4/3]',
        format === 'portrait' && 'aspect-[3/4]',
        format === 'square' && 'aspect-square',
        'transition-[aspect-ratio] duration-400 ease-out motion-reduce:transition-none',
        'bg-foreground/2 @lg:rounded-md',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

const ProductPageLayoutContent = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      className={cn(
        'border-border/8 bg-over dark:bg-under/8 overall:bg-under/8 text-foreground grow @md:overflow-hidden @md:rounded-xl @md:border',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

const ProductPageLayoutActions = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      className={cn(
        'border-border/4 inset-x-0 bottom-0 z-50 border-t p-3',
        'bg-over dark:bg-under/8 overall:bg-under/8',
        // Desktop: opaque sticky bar, full width (unchanged).
        '@lg:sticky @lg:w-full',
        // Mobile: `fixed` (not `sticky`) so the backdrop-blur actually paints
        '@max-lg:bg-background/72! sm:ml-2 @max-lg:fixed @max-lg:left-0 @max-lg:w-[100cqw] @max-lg:backdrop-blur-md @lg:ml-0!',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

const ProductPageLayout = Object.assign(ProductPageLayoutRoot, {
  Media: ProductPageLayoutMedia,
  Content: ProductPageLayoutContent,
  Actions: ProductPageLayoutActions,
})

export { ProductPageLayout }
